import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Loader2, Check, X as XIcon, Trophy, Zap, Target, Timer,
  Flame, Coins, TrendingUp, RotateCcw, Home, Award,
  Lightbulb,
} from "lucide-react";

const LEVELS = [
  { id: "auto", label: "Auto" },
  { id: "beginner", label: "Beginner" },
  { id: "easy", label: "Easy" },
  { id: "medium", label: "Medium" },
  { id: "hard", label: "Hard" },
  { id: "expert", label: "Expert" },
];

export default function BrainGamePlay() {
  const navigate = useNavigate();
  const { mode } = useParams();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [phase, setPhase] = useState("setup"); // setup|countdown|flash|answer|submitting|result
  const [difficulty, setDifficulty] = useState(() => { const l = new URLSearchParams(window.location.search).get("level"); return ["beginner", "easy", "medium", "hard", "expert"].includes(l) ? l : "auto"; });
  const [session, setSession] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [countdown, setCountdown] = useState(3);
  const [currentInput, setCurrentInput] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [result, setResult] = useState(null);
  const [modeName, setModeName] = useState(mode);
  const [home, setHome] = useState({ path: "/brain-gym", label: "Brain Gym" });

  const answersRef = useRef([]);
  const shownAtRef = useRef(0);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/brain-games/modes?hub=all`, { headers }).then((r) => {
      const m = (r.data.modes || []).find((x) => x.id === mode);
      if (m) {
        setModeName(m.name);
        if (m.hub === "mental") setHome({ path: "/mental-maths", label: "Mental Maths" });
        if (m.hub === "vedic") setHome({ path: "/vedic-maths", label: "Vedic Maths" });
      }
    }).catch(() => {});
    return () => clearInterval(timerRef.current);
  }, [mode]);

  // Refs mirror state so timer callbacks never read a stale question index/session
  const qIndexRef = useRef(0);
  const sessionRef = useRef(null);
  const flashRef = useRef(null);
  useEffect(() => () => clearTimeout(flashRef.current), []);

  const startGame = async (level = difficulty) => {
    try {
      const res = await axios.post(`${API}/brain-games/start`, { mode, difficulty: level, count: 10 }, { headers });
      sessionRef.current = res.data;
      qIndexRef.current = 0;
      setSession(res.data);
      answersRef.current = [];
      setAnswered(0);
      setQIndex(0);
      setPhase("countdown");
      setCountdown(3);
    } catch (e) {
      navigate(home.path);
    }
  };

  // Countdown 3..2..1..Go
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) { beginQuestion(0); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const beginQuestion = (idx) => {
    qIndexRef.current = idx;
    setQIndex(idx);
    setCurrentInput("");
    const q = sessionRef.current.questions[idx];
    if (q.type === "memory") {
      setPhase("flash");
      const ms = q.flash?.ms || 2500;
      clearTimeout(flashRef.current);
      flashRef.current = setTimeout(() => { startAnswer(); }, ms);
    } else {
      startAnswer();
    }
  };

  const startAnswer = () => {
    setPhase("answer");
    shownAtRef.current = Date.now();
    const per = sessionRef.current.time_per_question || 10;
    setTimeLeft(per);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          advance("");
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const advance = (value) => {
    clearInterval(timerRef.current);
    const s = sessionRef.current;
    const idx = qIndexRef.current;
    const q = s.questions[idx];
    if (answersRef.current.some((a) => a.question_id === q.id)) return;
    const resp = Date.now() - shownAtRef.current;
    answersRef.current = [...answersRef.current, { question_id: q.id, answer: String(value ?? ""), response_ms: resp }];
    if (String(value ?? "").trim()) setAnswered((a) => a + 1);
    const next = idx + 1;
    if (next < s.questions.length) {
      beginQuestion(next);
    } else {
      finish();
    }
  };

  const finish = async () => {
    setPhase("submitting");
    try {
      const res = await axios.post(`${API}/brain-games/submit`,
        { session_id: sessionRef.current.session_id, answers: answersRef.current }, { headers });
      setResult(res.data);
      setPhase("result");
    } catch (e) {
      navigate(home.path);
    }
  };

  const q = session?.questions?.[qIndex];
  const total = session?.questions?.length || 10;
  const progressPct = Math.round((qIndex / total) * 100);

  // ---------- SETUP ----------
  if (phase === "setup") {
    return (
      <Shell title={modeName} onBack={() => navigate(home.path)}>
        <div className="max-w-md mx-auto text-center pt-6" data-testid="game-setup">
          <div className="w-20 h-20 rounded-3xl bg-amber-400 flex items-center justify-center mx-auto mb-5">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-800 font-['Fredoka'] mb-1">{modeName}</h2>
          <p className="text-slate-500 text-sm mb-6">Pick a difficulty and beat your best score!</p>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Difficulty</p>
          <div className="grid grid-cols-3 gap-2 mb-8">
            {LEVELS.map((l) => (
              <button
                key={l.id}
                onClick={() => setDifficulty(l.id)}
                className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${difficulty === l.id ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}
                data-testid={`difficulty-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <Button onClick={() => startGame()} className="w-full h-14 text-lg rounded-2xl bg-amber-500 hover:bg-amber-600 font-['Fredoka']" data-testid="start-game-btn">
            Start Game
          </Button>
        </div>
      </Shell>
    );
  }

  // ---------- COUNTDOWN ----------
  if (phase === "countdown") {
    return (
      <Shell title={modeName} onBack={() => navigate(home.path)}>
        <div className="flex items-center justify-center pt-24" data-testid="game-countdown">
          <div key={countdown} className="text-8xl font-extrabold text-indigo-600 font-['Fredoka'] animate-bounce">
            {countdown > 0 ? countdown : "Go!"}
          </div>
        </div>
      </Shell>
    );
  }

  // ---------- SUBMITTING ----------
  if (phase === "submitting") {
    return (
      <Shell title={modeName}>
        <div className="flex flex-col items-center justify-center pt-24 text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <p>Scoring your game…</p>
        </div>
      </Shell>
    );
  }

  // ---------- RESULT ----------
  if (phase === "result" && result) {
    return (
      <Shell title="Results" onBack={() => navigate(home.path)}>
        <div className="max-w-md mx-auto pt-4" data-testid="game-result">
          {result.personal_best && (
            <div className="flex items-center justify-center gap-2 mb-3 text-amber-600 font-bold" data-testid="personal-best-badge">
              <Award className="w-5 h-5" /> New Personal Best!
            </div>
          )}
          <div className="rounded-3xl bg-white border-2 border-slate-100 p-6 text-center shadow-sm mb-4">
            <p className="text-sm text-slate-400 font-semibold uppercase tracking-wider">Score</p>
            <p className="text-6xl font-extrabold text-indigo-600 font-['Fredoka']" data-testid="result-score">{result.score}</p>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <Stat icon={Target} label="Accuracy" value={`${result.accuracy}%`} color="#10B981" />
              <Stat icon={Check} label="Correct" value={`${result.correct}/${result.total}`} color="#3B82F6" />
              <Stat icon={Flame} label="Best Combo" value={result.longest_combo} color="#F59E0B" />
              <Stat icon={Timer} label="Avg Time" value={`${(result.avg_response_ms / 1000).toFixed(1)}s`} color="#8B5CF6" />
              <Stat icon={TrendingUp} label="Speed" value={`${result.qpm}/min`} color="#06B6D4" />
              <Stat icon={Zap} label="XP Earned" value={`+${result.xp}`} color="#EAB308" />
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-4 text-amber-600 font-bold">
              <Coins className="w-4 h-4" /> +{result.coins} coins
            </div>
          </div>
          <AnswerReview details={result.details || []} questions={session?.questions || []} />
          <p className="text-center text-xs text-slate-500 mb-4">
            Next suggested level: <span className="font-bold capitalize text-slate-700">{result.next_difficulty}</span>
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => navigate(home.path)} className="h-12 rounded-2xl" data-testid="result-home-btn">
              <Home className="w-4 h-4 mr-2" /> {home.label}
            </Button>
            <Button onClick={() => { setDifficulty(result.next_difficulty); setResult(null); startGame(result.next_difficulty); }} className="h-12 rounded-2xl bg-amber-500 hover:bg-amber-600" data-testid="play-again-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Play Again
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ---------- FLASH / ANSWER ----------
  const perTime = session?.time_per_question || 10;
  return (
    <Shell title={modeName} onBack={() => navigate(home.path)}>
      <div className="max-w-md mx-auto pt-2" data-testid="game-play">
        {/* progress + counter */}
        <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
          <span data-testid="question-counter">Question {qIndex + 1} / {total}</span>
          <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> {answered}</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-6">
          <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>

        {phase === "flash" && q?.flash && (
          <div className="text-center py-6" data-testid="flash-screen">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Memorise!</p>
            {q.flash.grid ? (
              <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                {q.flash.numbers.map((n, i) => (
                  <div key={i} className="aspect-square rounded-2xl bg-indigo-100 flex items-center justify-center text-3xl font-extrabold text-indigo-700 font-['Fredoka']">{n}</div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-3">
                {q.flash.numbers.map((n, i) => (
                  <span key={i} className="text-5xl font-extrabold text-indigo-600 font-['Fredoka']">{n}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {phase === "answer" && q && (
          <div data-testid="answer-screen">
            {/* per-question timer */}
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-6">
              <div className="h-full bg-amber-400 transition-all duration-1000 ease-linear" style={{ width: `${(timeLeft / perTime) * 100}%` }} />
            </div>

            <div className="rounded-3xl bg-white border-2 border-slate-100 p-8 text-center shadow-sm mb-6 min-h-[120px] flex items-center justify-center">
              <p className="text-4xl font-extrabold text-slate-800 font-['Fredoka']" data-testid="question-prompt">
                {q.type === "memory" ? (q.recall_prompt || "Recall the numbers") : <>{q.prompt}{q.type === "input" && !q.no_eq ? " = ?" : ""}</>}
              </p>
            </div>

            {(q.type === "input" || q.type === "memory") && (
              <form onSubmit={(e) => { e.preventDefault(); if (currentInput.trim()) advance(currentInput.trim()); }}>
                <Input
                  ref={inputRef}
                  value={currentInput}
                  onChange={(e) => setCurrentInput(e.target.value)}
                  placeholder={q.type === "memory" ? "Type your answer" : "Your answer"}
                  inputMode={q.type === "memory" ? "text" : "numeric"}
                  className="h-16 text-center text-2xl font-bold rounded-2xl mb-4"
                  data-testid="answer-input"
                  autoFocus
                />
                <Button type="submit" className="w-full h-14 text-lg rounded-2xl bg-indigo-600 hover:bg-indigo-700 font-['Fredoka']" data-testid="answer-submit-btn">
                  Submit
                </Button>
              </form>
            )}

            {(q.type === "choice" || q.type === "truefalse") && (
              <div className={`grid ${q.type === "truefalse" ? "grid-cols-2" : "grid-cols-2"} gap-3`}>
                {(q.choices || []).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => advance(c)}
                    className="h-16 rounded-2xl bg-white border-2 border-slate-200 text-xl font-bold text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 transition-all active:scale-95 font-['Fredoka'] capitalize"
                    data-testid={`choice-${i}`}
                  >
                    {q.type === "truefalse" ? (c === "true" ? "✓ Correct" : "✗ Wrong") : c}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ title, onBack, children }) {
  return (
    <div className="min-h-screen bg-slate-50 font-['Lexend']">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="game-back-btn">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
          )}
          <h1 className="text-base font-bold text-slate-800 font-['Fredoka']">{title}</h1>
        </div>
      </header>
      <main className="px-4 py-5">{children}</main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3 flex flex-col items-center">
      <Icon className="w-5 h-5 mb-1" style={{ color }} />
      <p className="text-lg font-extrabold text-slate-800 font-['Fredoka'] leading-none">{value}</p>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

const AnswerReview = ({ details, questions }) => {
  const [open, setOpen] = useState(false);
  if (!details.length) return null;
  const byId = Object.fromEntries(questions.map((q) => [q.id, q]));
  const label = (q) => (q?.type === "memory" ? (q.flash?.numbers || []).join("  ") : q?.prompt || "");
  const fmt = (v) => (v === "true" ? "True" : v === "false" ? "False" : v || "—");
  return (
    <div className="rounded-3xl bg-white border-2 border-slate-100 p-4 mb-4" data-testid="answer-review">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between font-bold text-slate-700" data-testid="answer-review-toggle">
        Review answers {details.some((d) => d.explain) ? "& tricks" : ""} <span className="text-xs text-slate-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <ol className="mt-3 space-y-2">
          {details.map((d, i) => (
            <li key={d.question_id} className={`rounded-2xl p-3 text-sm ${d.correct ? "bg-emerald-50" : "bg-rose-50"}`} data-testid={`review-item-${i}`}>
              <p className="font-semibold text-slate-800">{i + 1}. {label(byId[d.question_id])}</p>
              <p className="text-xs mt-0.5">{d.correct ? <span className="text-emerald-700 font-semibold">Your answer ✓ {fmt(d.correct_answer)}</span>
                : <><span className="text-rose-700 font-semibold">You: {fmt(d.your_answer)}</span> · <span className="text-emerald-700 font-semibold">Answer: {fmt(d.correct_answer)}</span></>}</p>
              {d.explain && <p className="text-xs text-slate-600 mt-1 flex gap-1" data-testid={`review-explain-${i}`}><Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />{d.explain}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
